<template>
  <div class="take-over--editor-info" v-if="!recentUpdate">
    <aha-icon
      name="info-circle-o"
      class="mt-1.5"
      viewBox="0 0 22 22"
      width="22"
      height="22"
      maintainColor="false"
      color="#9BB3E9"
    />

    <p class="take-over--editor-text">
      {{ $i18nPresenter.t('You can view and present this presentation.') }}
    </p>
  </div>
  <div class="take-over--editor-info" v-else>
    <aha-icon
      name="warn-circle-o"
      class="mt-1.5"
      color="#FF7747"
      viewBox="0 0 22 22"
      width="22"
      height="22"
      maintainColor="false"
    />
    <p class="take-over--editor-text">
      {{ $i18nPresenter.t('The original presentation has been updated.') }}
      <a
        class="font-bold text-link float-right"
        @click="showConfirmUpdateModal()"
        v-tooltip="$i18nPresenter.t('Get the latest slides and reset results. Your current results will be saved in Report')"
      >
        {{ $i18nPresenter.t('Get the latest') }}
      </a>
    </p>
    <aha-modal v-if="isConfirmingUpdate" @hide="hideConfirmUpdateModal()" :title="$i18nPresenter.t('Get the latest version')">
      <div>
        {{ $i18nPresenter.t('Getting a new version will clear all votes, reactions, and responses, but don’t worry — your results will be saved in Report.') }}
      </div>

      <template #action>
        <aha-antd-button
          v-aha-emit-action
          name="presentation-update-cancel"
          :disabled="isUpdating"
          variant="secondary"
          class="mr-2"
          @click="hideConfirmUpdateModal()"
        >
          <span>{{ $i18nPresenter.t("Cancel") }}</span>
        </aha-antd-button>
        <aha-antd-button
          v-aha-emit-action
          name="presentation-update"
          variant="primary"
          :disabled="isUpdating"
          is-loading-inline
          @click="syncPresentationWithRoot()"
          :submitting="isUpdating"
        >
          {{ $i18nPresenter.t("Get the latest") }}
        </aha-antd-button>
      </template>
    </aha-modal>
  </div>
</template>

<script>
import moment from 'moment'
import * as Sentry from '@sentry/vue'
import PresentationApi from '@/api/presentation'
import VueToastedUtils from '@/utils/vue-toasted.utils.js'
import eventCategories from '@/constant/eventCategories'
import Utils from '@/util'

const shouldToastSessionStorageKey = 'shared-presentation-updated'
export default {
  name: 'PresentationViewOnlyInfoBar',
  props: {
    presentation: {
      type: Object,
      required: true,
    },
    currentUser: {
      type: Object,
    },
  },
  data() {
    return {
      recentUpdate: '',
      isUpdating: false,
      isConfirmingUpdate: false,
    }
  },
  mounted() {
    this.checkForUpdate()
    // Optionally, poll every 30s:
    this._interval = setInterval(this.checkForUpdate, 30000)
    if (sessionStorage.getItem(shouldToastSessionStorageKey)) {
      // show toast
      VueToastedUtils.showToast(this.$i18nPresenter.t('You are now on the latest version'), {}, {
        hasReturnValue: true,
        duration: 10000,
        action: {
          text: this.$i18nPresenter.t('View saved sessions'),
          onClick: () => {
            this.$router.push({
              name: 'PresentationReport',
              params: { id: this.presentation.id },
            })
            this.isShowReport = true
            Utils.trackGA4AndMixpanel(eventCategories.PRESENTATION_EDITOR, {
              eventAction: 'Editor > Result results toaster',
              eventCategory: eventCategories.PRESENTATION_EDITOR,
              ...{
                presentationId: this.presentation.id,
              },
            })
          },
        },
      })
      sessionStorage.removeItem(shouldToastSessionStorageKey)
    }
  },
  beforeDestroy() {
    if (this._interval) clearInterval(this._interval)
  },
  methods: {
    async checkForUpdate() {
      try {
        const res = await PresentationApi.getRootPresentationUpdateTime(this.presentation.id)
        if (res &&
          res.rootLastUpdatedAt &&
          res.clonedRefreshedAt &&
          new Date(res.rootLastUpdatedAt) > new Date(res.clonedRefreshedAt)
        ) {
          this.recentUpdate = moment(res.rootLastUpdatedAt).fromNow()
        } else {
          this.recentUpdate = ''
        }
      } catch (e) {
        Sentry.captureException(e)
        // On error, hide the button
        this.recentUpdate = ''
      }
    },

    async syncPresentationWithRoot() {
      this.isUpdating = true
      await PresentationApi.syncWithRootPresentation(this.presentation.id)
      // let the component know that it should show success toast
      // after reloading the page
      sessionStorage.setItem(shouldToastSessionStorageKey, true)
      this.$emit('reload')
    },

    hideConfirmUpdateModal() {
      this.isConfirmingUpdate = false
    },

    showConfirmUpdateModal() {
      this.isConfirmingUpdate = true
    },
  },
}
</script>
